import { makeAutoObservable } from "mobx";

class TargetSetupUIStore {
  loadingDisclosures = false;
  loadingFrameworks = false;
  loadingOrgTree = false;
  loadingTargets = false;
  savingTarget = false;
  bulkSetting = false;

  /** @type {Set<string>} expanded disclosure IDs */
  expandedDisclosures = new Set();

  selectedOrgsByDisclosure = new Map();
  targetFrequencyByDisclosure = new Map();
  rowSaveHandlersByDisclosure = new Map();

  showSetValueToAllDialog = false;
  setValueToAllDisclosure = null;

  constructor() {
    makeAutoObservable(this);
  }

  setLoadingDisclosures = (v) => {
    this.loadingDisclosures = v;
  };

  setLoadingFrameworks = (v) => {
    this.loadingFrameworks = v;
  };

  setLoadingOrgTree = (v) => {
    this.loadingOrgTree = v;
  };

  setLoadingTargets = (v) => {
    this.loadingTargets = v;
  };

  setSavingTarget = (v) => {
    this.savingTarget = v;
  };

  setBulkSetting = (v) => {
    this.bulkSetting = v;
  };

  getSelectedOrgs = (disclosureKey) => {
    return this.selectedOrgsByDisclosure.get(disclosureKey) || [];
  };

  setSelectedOrgs = (disclosureKey, v) => {
    if (!disclosureKey) return;
    if (Array.isArray(v) && v.length === 0) {
      this.selectedOrgsByDisclosure.delete(disclosureKey);
      return;
    }
    this.selectedOrgsByDisclosure.set(disclosureKey, v);
  };

  getDisclosureTargetFrequency = (disclosureKey, fallback = "yearly") => {
    if (!disclosureKey) return fallback === "monthly" ? "monthly" : "yearly";
    return (
      this.targetFrequencyByDisclosure.get(disclosureKey) ||
      (fallback === "monthly" ? "monthly" : "yearly")
    );
  };

  setDisclosureTargetFrequency = (disclosureKey, frequency) => {
    if (!disclosureKey) return;
    this.targetFrequencyByDisclosure.set(
      disclosureKey,
      frequency === "monthly" ? "monthly" : "yearly",
    );
  };

  registerRowSaveHandler = (disclosureKey, rowKey, handler) => {
    if (!disclosureKey || !rowKey || typeof handler !== "function") return;
    if (!this.rowSaveHandlersByDisclosure.has(disclosureKey)) {
      this.rowSaveHandlersByDisclosure.set(disclosureKey, new Map());
    }
    this.rowSaveHandlersByDisclosure.get(disclosureKey).set(rowKey, handler);
  };

  unregisterRowSaveHandler = (disclosureKey, rowKey) => {
    const handlers = this.rowSaveHandlersByDisclosure.get(disclosureKey);
    if (!handlers) return;
    handlers.delete(rowKey);
    if (handlers.size === 0) {
      this.rowSaveHandlersByDisclosure.delete(disclosureKey);
    }
  };

  savePendingRowsForDisclosure = async (disclosureKey) => {
    const handlers = this.rowSaveHandlersByDisclosure.get(disclosureKey);
    if (!handlers) return true;
    const results = await Promise.all(
      Array.from(handlers.values()).map((handler) => handler()),
    );
    return results.every((result) => result !== false);
  };

  toggleExpandDisclosure = (disclosureId) => {
    if (this.expandedDisclosures.has(disclosureId)) {
      this.expandedDisclosures.delete(disclosureId);
    } else {
      this.expandedDisclosures.add(disclosureId);
    }
  };

  isExpanded = (disclosureId) => {
    return this.expandedDisclosures.has(disclosureId);
  };

  openSetValueToAllDialog = (disclosure) => {
    this.setValueToAllDisclosure = disclosure;
    this.showSetValueToAllDialog = true;
  };

  closeSetValueToAllDialog = () => {
    this.showSetValueToAllDialog = false;
    this.setValueToAllDisclosure = null;
  };

  reset = () => {
    this.loadingDisclosures = false;
    this.loadingFrameworks = false;
    this.loadingOrgTree = false;
    this.loadingTargets = false;
    this.savingTarget = false;
    this.bulkSetting = false;
    this.expandedDisclosures = new Set();
    this.selectedOrgsByDisclosure = new Map();
    this.targetFrequencyByDisclosure = new Map();
    this.rowSaveHandlersByDisclosure = new Map();
    this.showSetValueToAllDialog = false;
    this.setValueToAllDisclosure = null;
  };
}

export const targetSetupUIStore = new TargetSetupUIStore();
